/** Mise en pages A4 de la mise en ambiance (demande d'Alexis du 02/10/2026) : le document s'affiche en feuilles
 *  A4 successives, comme l'OPO, et non en une longue feuille. Fonctions pures, sans DOM : la vue mesure (hauteur
 *  des blocs, nombre de lignes d'un extrait de paragraphe) et ce module répartit les blocs dans les pages.
 *
 *  Gabarit repris du PDF (server/generex_api/briefing.py, `to_pdf`) : A4 595 × 842 pt, cadre de texte à 30 pt
 *  du bord gauche, 25 pt du bord droit, 86 pt du haut et 62 pt du bas ; titres liés au texte qui les suit,
 *  espace avant un titre ignoré en haut de page (comme ReportLab). */

/** Page A4 et cadre du texte du PDF, en points. */
export const PAGE = { width: 595, height: 842, left: 30, right: 25, top: 86, bottom: 62 } as const;
export const FRAME_WIDTH = PAGE.width - PAGE.left - PAGE.right;
export const FRAME_HEIGHT = PAGE.height - PAGE.top - PAGE.bottom;

/** Bloc insécable (titre, rangée d'en-tête, éditeur, attente de l'IA…), de hauteur mesurée. */
export interface BlockItem {
  kind: "block";
  key: string;
  height: number;
  /** Espaces avant et après (ReportLab : spaceBefore, spaceAfter), dans l'unité des hauteurs. */
  before: number;
  after: number;
  /** Titre : jamais seul en bas de page, il suit le début du bloc suivant. */
  keepWithNext?: boolean;
  /** Peut aller dans la colonne à côté d'une carte (`FloatItem`) : intitulé d'une ligne, de même hauteur à toute
   *  largeur, si la colonne a au moins `minWidth` de large. */
  beside?: boolean;
  minWidth?: number;
  /** En-tête de la fiche pays : toujours dans la colonne à côté de la carte, qui descend sous elle au besoin. */
  pinned?: boolean;
}

/** Paragraphe sécable entre deux mots : `words` mots, lignes de `lineHeight`. */
export interface TextItem {
  kind: "text";
  key: string;
  words: number;
  lineHeight: number;
  before: number;
  after: number;
  /** Indices des mots où une page peut commencer (défaut : tous) ; jamais au milieu d'une désignation d'unité. */
  breaks?: number[];
  /** Paragraphe en cours d'écriture par l'IA : la règle de la veuve ne s'applique pas, sinon une ligne passerait
   *  d'une feuille à l'autre, aller et retour, à mesure que le texte grandit (elle s'applique une fois le texte fini). */
  live?: boolean;
  /** En-tête de la fiche pays : entier dans la colonne à côté de la carte, qui descend sous elle au besoin. */
  pinned?: boolean;
}

/** Carte posée d'un côté du cadre (fiche pays, `country_layout.py`) : ce qui la suit coule dans la colonne qui
 *  reste à côté d'elle tant qu'il y a la place (un paragraphe s'y coupe entre deux lignes, un intitulé y va avec le
 *  début de son texte), puis reprend dessous en pleine largeur ; un bloc qui n'y va pas (rangée de chiffres, autre
 *  carte) arrête la colonne. En bas de page, la carte se réduit jusqu'à `minScale` ; au-delà, elle passe à la page
 *  suivante. */
export interface FloatItem {
  kind: "float";
  key: string;
  width: number;
  height: number;
  side: "left" | "right";
  /** Écart entre la carte et la colonne à côté d'elle. */
  gap: number;
  /** Espace sous la carte, avant ce qui reprend en pleine largeur. */
  below: number;
  minScale: number;
  before: number;
  after: number;
}

export type FlowItem = BlockItem | TextItem | FloatItem;

/** Nombre de lignes de l'extrait [from, to[ du paragraphe à la largeur `width` (celle du cadre par défaut),
 *  mesuré par la vue. */
export type LineCounter = (item: TextItem, from: number, to: number, width?: number) => number;

/** Morceau placé : un bloc entier, une carte, ou un fragment de paragraphe (mots [from, to[). */
export interface Piece {
  key: string;
  /** Rang du fragment dans son paragraphe (0 pour un bloc). */
  part: number;
  page: number;
  /** Position dans le cadre de texte de la page : haut, bord gauche et largeur (colonne à côté d'une carte, ou
   *  largeur du cadre). */
  top: number;
  left: number;
  width: number;
  height: number;
  from: number;
  to: number;
  /** Premier / dernier fragment de l'élément. */
  first: boolean;
  last: boolean;
}

export interface Paged {
  /** Hauteur occupée dans le cadre de chaque page (peut dépasser le cadre : éditeur plus haut qu'une page). */
  pages: number[];
  pieces: Piece[];
}

/** Règles de la mise en pages. Par défaut, celles de la mise en ambiance ; la fiche pays reprend exactement
 *  celles de son PDF (`country_layout.py`). */
export interface PaginateOptions {
  /** Largeur du cadre (colonne à côté d'une carte, largeur des morceaux posés) ; 0 sans carte. */
  width?: number;
  /** Entre deux éléments, le plus grand de l'espace après l'un et de l'espace avant l'autre (ReportLab :
   *  overlapAttachedSpace), au lieu de leur somme. */
  mergeSpaces?: boolean;
  /** Veuve évitée comme ReportLab (allowWidows=0) : un paragraphe de trois lignes ou moins qui laisserait une ligne
   *  seule en haut de la page suivante y part entier. */
  strictWidows?: boolean;
}

const EPS = 0.5;

/** Hauteur minimale du début d'un élément pour qu'un titre le précède sur la même page : le bloc entier, deux
 *  lignes du paragraphe (une seule s'il n'en a qu'une), la carte à sa plus petite taille ; pour un titre, la chaîne
 *  des titres qui suivent. */
function startNeed(items: FlowItem[], i: number, lines: LineCounter, merge: boolean): number {
  const item = items[i]!;
  if (item.kind === "text") return Math.min(2, Math.max(1, lines(item, 0, item.words))) * item.lineHeight;
  if (item.kind === "float") return item.height * item.minScale;
  let need = item.height;
  if (item.keepWithNext && i + 1 < items.length) {
    const next = items[i + 1]!;
    need += (merge ? Math.max(item.after, next.before) : item.after + next.before) + startNeed(items, i + 1, lines, merge);
  }
  return need;
}

/** Plus grand début de page b de `breaks` (b > from) tel que [from, b[ tienne en `maxLines` lignes ; -1 sinon. */
function fit(item: TextItem, from: number, maxLines: number, breaks: number[], lines: LineCounter, width?: number): number {
  let lo = 0;
  let hi = breaks.length - 1;
  while (lo <= hi && breaks[lo]! <= from) lo++;
  let best = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const b = breaks[mid]!;
    if (lines(item, from, b, width) <= maxLines) {
      best = b;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return best;
}

/** Répartit les éléments dans des pages de `frameHeight` : blocs entiers, paragraphes coupés entre deux mots
 *  (le fragment remplit la place restante, la suite ouvre la page suivante), titres liés au texte qui suit,
 *  ni ligne seule en bas de page (orpheline) ni ligne seule en haut de la suivante (veuve) quand c'est possible ;
 *  à côté d'une carte, la colonne qui reste (`FloatItem`). */
export function paginate(items: FlowItem[], frameHeight: number, lines: LineCounter, options: PaginateOptions = {}): Paged {
  const frameWidth = options.width ?? 0;
  const merge = Boolean(options.mergeSpaces);
  const pages: number[] = [0];
  const pieces: Piece[] = [];
  let used = 0;
  let empty = true;
  let after = 0;
  // Colonne à côté d'une carte posée sur la page : bord gauche, largeur, bas de la carte, espace sous elle.
  let column: { left: number; width: number; bottom: number; below: number; first: boolean } | null = null;
  const newPage = () => {
    pages.push(0);
    used = 0;
    empty = true;
    after = 0;
    column = null;
  };
  const place = (piece: Omit<Piece, "page" | "top" | "left" | "width">, gap: number, left = 0, width = frameWidth) => {
    const top = used + gap;
    pieces.push({ ...piece, page: pages.length - 1, top, left, width });
    used = top + piece.height;
    pages[pages.length - 1] = Math.max(pages[pages.length - 1]!, used);
    empty = false;
  };
  const gapOf = (item: FlowItem) => {
    if (empty || column?.first) return 0;
    return merge ? Math.max(after, item.before) : after + item.before;
  };
  // La colonne s'arrête : la suite reprend sous la carte, en pleine largeur.
  const clear = () => {
    if (!column) return;
    used = Math.max(used, column.bottom);
    after = column.below;
    column = null;
  };
  // Intitulé dans la colonne : seulement si le paragraphe qui le suit y commence aussi (entier, ou deux lignes au
  // moins sans en laisser une seule sous la carte) ; sinon ils passent ensemble sous la carte.
  const headingFits = (i: number, room: number): boolean => {
    const item = items[i] as BlockItem;
    const next = items[i + 1];
    if (!column) return false;
    if (!next) return item.height <= room + EPS;
    if (next.kind !== "text") return false;
    const space = merge ? Math.max(item.after, next.before) : item.after + next.before;
    const left = room - item.height - space;
    const total = lines(next, 0, next.words, column.width);
    if (total * next.lineHeight <= left + EPS) return true;
    let maxLines = Math.floor((left + EPS) / next.lineHeight);
    if (total - maxLines === 1) maxLines = total > 3 ? maxLines - 1 : 0;
    return maxLines >= 2;
  };

  items.forEach((item, i) => {
    if (item.kind === "float") {
      clear();
      let gap = gapOf(item);
      let { width, height } = item;
      const room = frameHeight - used - gap;
      if (height > room + EPS && !empty) {
        const scale = room / height;
        if (scale >= item.minScale) {
          width *= scale;
          height *= scale;
        } else {
          newPage();
          gap = 0;
        }
      }
      const left = item.side === "left" ? 0 : frameWidth - width;
      place({ key: item.key, part: 0, height, from: 0, to: 0, first: true, last: true }, gap, left, width);
      const top = used - height;
      column = {
        left: item.side === "left" ? width + item.gap : 0,
        width: Math.max(0, frameWidth - width - item.gap),
        bottom: top + height,
        below: item.below,
        first: true,
      };
      used = top;
      after = 0;
      return;
    }
    if (item.kind === "block") {
      if (column && item.pinned) {
        place({ key: item.key, part: 0, height: item.height, from: 0, to: 0, first: true, last: true }, gapOf(item), column.left, column.width);
        column.first = false;
        column.bottom = Math.max(column.bottom, used);
        after = item.after;
        return;
      }
      if (column) {
        const room = column.bottom - used - gapOf(item);
        const roomy = column.width + EPS >= (item.minWidth ?? 0);
        if (item.beside && roomy && (item.keepWithNext ? headingFits(i, room) : item.height <= room + EPS)) {
          place({ key: item.key, part: 0, height: item.height, from: 0, to: 0, first: true, last: true }, gapOf(item), column.left, column.width);
          column.first = false;
          after = item.after;
          return;
        }
        clear();
      }
      const need = startNeed(items, i, lines, merge);
      if (!empty && used + gapOf(item) + need > frameHeight + EPS) newPage();
      place({ key: item.key, part: 0, height: item.height, from: 0, to: 0, first: true, last: true }, gapOf(item));
      after = item.after;
      return;
    }
    if (item.words <= 0) return;
    const breaks = item.breaks ?? Array.from({ length: item.words - 1 }, (_, w) => w + 1);
    let from = 0;
    let part = 0;
    let guard = 0;
    if (column && item.pinned) {
      // En-tête : entier dans la colonne, qui descend sous la carte au besoin.
      const height = lines(item, 0, item.words, column.width) * item.lineHeight;
      place({ key: item.key, part: 0, height, from: 0, to: item.words, first: true, last: true }, gapOf(item), column.left, column.width);
      column.first = false;
      column.bottom = Math.max(column.bottom, used);
      after = item.after;
      return;
    }
    while (from < item.words && guard++ < 10_000) {
      if (column) {
        // À côté de la carte : la colonne, jusqu'au bas de la carte ; la suite reprend dessous.
        const gap = gapOf(item);
        const room = column.bottom - used - gap;
        const total = lines(item, from, item.words, column.width);
        if (total * item.lineHeight <= room + EPS) {
          const height = total * item.lineHeight;
          place({ key: item.key, part, height, from, to: item.words, first: part === 0, last: true }, gap, column.left, column.width);
          column.first = false;
          after = item.after;
          return;
        }
        let maxLines = Math.floor((room + EPS) / item.lineHeight);
        // Ni ligne seule au bas de la colonne, ni ligne seule sous la carte (ReportLab : orphelines et veuves).
        if (total - maxLines === 1) maxLines = total > 3 ? maxLines - 1 : 0;
        const end = maxLines >= 2 ? fit(item, from, maxLines, breaks, lines, column.width) : -1;
        if (end < 0 || end >= item.words) {
          clear();
          continue;
        }
        const got = lines(item, from, end, column.width);
        place({ key: item.key, part, height: got * item.lineHeight, from, to: end, first: part === 0, last: false }, gap, column.left, column.width);
        clear();
        from = end;
        part += 1;
        continue;
      }
      const gap = gapOf(item);
      const total = lines(item, from, item.words);
      const height = total * item.lineHeight;
      if (used + gap + height <= frameHeight + EPS) {
        place({ key: item.key, part, height, from, to: item.words, first: part === 0, last: true }, gap);
        after = item.after;
        return;
      }
      let maxLines = Math.floor((frameHeight - used - gap + EPS) / item.lineHeight);
      // Orpheline : une ligne seule en bas de page part avec la suite, sauf en haut d'une page vide.
      if (maxLines < 2 && total >= 2 && !empty) {
        newPage();
        continue;
      }
      // Veuve : une seule ligne restante en haut de la page suivante ; on y envoie une ligne de plus (paragraphe
      // court, `strictWidows` : il part entier).
      if (!item.live && total - maxLines === 1) {
        if (maxLines >= 3) maxLines -= 1;
        else if (options.strictWidows && !empty) {
          newPage();
          continue;
        }
      }
      let end = maxLines > 0 ? fit(item, from, maxLines, breaks, lines) : -1;
      if (end < 0) {
        if (!empty) {
          newPage();
          continue;
        }
        // Page vide trop petite pour une ligne (ou mot insécable) : au moins un début de page plus loin.
        end = breaks.find((b) => b > from) ?? item.words;
      }
      if (end >= item.words) {
        place({ key: item.key, part, height, from, to: item.words, first: part === 0, last: true }, gap);
        after = item.after;
        return;
      }
      const got = lines(item, from, end);
      place({ key: item.key, part, height: got * item.lineHeight, from, to: end, first: part === 0, last: false }, gap);
      newPage();
      from = end;
      part += 1;
    }
  });
  return { pages, pieces };
}

/** Mots d'un texte : positions [début, fin[ de chaque suite de caractères non blancs. */
export function wordSpans(text: string): [number, number][] {
  const spans: [number, number][] = [];
  for (const m of text.matchAll(/\S+/g)) spans.push([m.index, m.index + m[0].length]);
  return spans;
}

/** Débuts de page permis (indices de mots, sauf le premier) : jamais à l'intérieur d'un passage insécable
 *  (`unbreakable`, recherché sur tout le texte : désignations d'unités, GDH « 070600B OCT 26 »), ni après une espace
 *  insécable (« 212 000 », « Capitale : ») : le navigateur n'y coupe jamais la ligne. */
export function pageBreaks(text: string, spans: [number, number][], unbreakable?: RegExp): number[] {
  const locked: [number, number][] = [];
  if (unbreakable) {
    const re = new RegExp(unbreakable.source, unbreakable.flags.includes("g") ? unbreakable.flags : `${unbreakable.flags}g`);
    for (const m of text.matchAll(re)) locked.push([m.index, m.index + m[0].length]);
  }
  const out: number[] = [];
  for (let w = 1; w < spans.length; w++) {
    const start = spans[w]![0];
    if (/^[\u00a0\u202f]+$/.test(text.slice(spans[w - 1]![1], start))) continue;
    if (!locked.some(([a, b]) => start > a && start < b)) out.push(w);
  }
  return out;
}

/** Texte des mots [from, to[, avec ses blancs intérieurs (retours à la ligne compris). */
export function sliceWords(text: string, spans: [number, number][], from: number, to: number): string {
  const a = Math.max(0, Math.min(from, spans.length));
  const b = Math.max(a, Math.min(to, spans.length));
  if (a >= b) return "";
  return text.slice(spans[a]![0], spans[b - 1]![1]);
}

/** Fragment coupé en bas de page dont le texte contient un retour à la ligne simple : seule sa toute dernière
 *  ligne doit être justifiée (text-align-last vaut aussi pour toute ligne finie par un retour forcé, ce que le PDF
 *  ne fait pas). Renvoie le texte avant le dernier retour (sans lui) et la dernière portion, ou null s'il n'y en a
 *  pas. Les coupures de lignes sont les mêmes que celles du texte entier : la mesure reste valable. */
export function splitAtLastBreak(text: string): [string, string] | null {
  const at = text.lastIndexOf("\n");
  if (at < 0) return null;
  const head = text.slice(0, at).replace(/[ \t]+$/, "");
  const tail = text.slice(at + 1).replace(/^\s+/, "");
  if (!head.trim() || !tail) return null;
  return [head, tail];
}

/** Position verticale de chaque feuille dans la pile (même écart que l'OPO) et hauteur totale. Une feuille garde
 *  le format A4 ; elle ne s'allonge que si son contenu dépasse le cadre (éditeur plus haut qu'une page). */
export function stack(
  used: number[],
  sheetHeight: number,
  frameTop: number,
  frameHeight: number,
  frameBottom: number,
  gap: number,
): { tops: number[]; heights: number[]; total: number } {
  const tops: number[] = [];
  const heights: number[] = [];
  let y = 0;
  used.forEach((h, i) => {
    const height = h > frameHeight + EPS ? frameTop + h + frameBottom : sheetHeight;
    if (i > 0) y += gap;
    tops.push(y);
    heights.push(height);
    y += height;
  });
  return { tops, heights, total: y };
}

/** Lignes non vides d'un texte (rubrique « Libellé : valeur » de la fiche pays : une entrée par ligne). */
export function textLines(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

/** Longueur du libellé d'une ligne « Libellé : valeur », deux-points compris (« Capitale : » → 10) ; 0 sans libellé.
 *  Même découpe que le PDF (server/generex_api/country_sheet.py, `field_parts`). */
export function fieldLabelLength(line: string): number {
  const found = /^[^:\n]{1,60}?\s*:(?=\s)/.exec(line);
  return found ? found[0].length : 0;
}

/** Éléments d'une liste à puces (fiche pays) : une ligne par élément, puce « - » (ou « • ») retirée. */
export function listItems(text: string): string[] {
  return textLines(text)
    .map((line) => line.replace(/^[-–•*]\s*/, "").trim())
    .filter(Boolean);
}
